import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Plus, KeyRound, IdCard, Printer, ShieldAlert } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import DataTable from '../components/ui/DataTable';
import TeamMemberService from '../services/TeamMemberService';
import RoleService from '../services/RoleService';
import MemberStatusService from '../services/MemberStatusService';
import MemberBadgeService from '../services/MemberBadgeService';
import AccessTierService from '../services/AccessTierService';
import PasswordRequirements from '../components/ui/PasswordRequirements';
import { passwordProblem } from '../utils/passwordPolicy';

const ROLE_LABELS = { admins: 'Admin', doctors: 'Doctor', nurses: 'Nurse', rsos: 'RSO' };

const EMPTY_FORM = {
  fullname: '',
  email: '',
  mobile: '',
  gender: 'male',
  address: '',
  experience: '',
  emirateId: '',
  roleId: '',
  memberStatusId: '',
  accessTierId: '',
  password: '',
};

export default function Users() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [accessTiers, setAccessTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [resetRow, setResetRow] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [badgeRow, setBadgeRow] = useState(null);
  const [badgeLoading, setBadgeLoading] = useState(false);
  const [badgeStatus, setBadgeStatus] = useState(null);
  const [issuedBadge, setIssuedBadge] = useState(null);
  const [badgeBusy, setBadgeBusy] = useState(false);

  const load = async () => {
    try {
      const [members, roleData, statusData, accessTierData] = await Promise.all([
        TeamMemberService.findAll(),
        RoleService.findAll(),
        MemberStatusService.findAll(),
        AccessTierService.findAll(),
      ]);
      setRows(members.data);
      setRoles(roleData.data);
      setStatuses(statusData.data);
      setAccessTiers(accessTierData.data);
    } catch {
      toast.error('Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      ...EMPTY_FORM,
      fullname: row.fullname || '',
      email: row.email || '',
      mobile: row.mobile || '',
      gender: row.gender || 'male',
      address: row.address || '',
      experience: row.experience ?? '',
      emirateId: row.emirateId || '',
      roleId: row.roleId || '',
      memberStatusId: row.memberStatusId || '',
      accessTierId: row.accessTierId || '',
      password: '',
    });
    setEditingId(row.id);
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.fullname || !form.email || !form.roleId || !form.accessTierId) {
      toast.error('Full name, email, role and access tier are required.');
      return;
    }
    if (!editingId && !form.password) {
      toast.error('A password is required for new users.');
      return;
    }
    if (!editingId && passwordProblem(form.password)) {
      toast.error(passwordProblem(form.password));
      return;
    }
    const payload = { ...form };
    if (!payload.password) delete payload.password;
    if (payload.experience === '') delete payload.experience;
    if (payload.memberStatusId === '') delete payload.memberStatusId;

    try {
      if (editingId) {
        await TeamMemberService.update(editingId, payload);
        toast.success('User updated.');
      } else {
        await TeamMemberService.create(payload);
        toast.success('User created.');
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed.');
    }
  };

  const handleDelete = async () => {
    try {
      await TeamMemberService.delete(deleteRow.id);
      toast.success('User deleted.');
      setDeleteRow(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed.');
    }
  };

  const handleResetPassword = async () => {
    const problem = passwordProblem(resetPassword);
    if (problem) {
      toast.error(problem);
      return;
    }
    try {
      await TeamMemberService.update(resetRow.id, { password: resetPassword });
      toast.success(`Password reset for ${resetRow.fullname}.`);
      setResetRow(null);
      setResetPassword('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Password reset failed.');
    }
  };

  const openBadge = async (row) => {
    setBadgeRow(row);
    setIssuedBadge(null);
    setBadgeLoading(true);
    try {
      const res = await MemberBadgeService.getBadge(row.id);
      setBadgeStatus(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load badge status.');
      setBadgeStatus(null);
    } finally {
      setBadgeLoading(false);
    }
  };

  const closeBadge = () => {
    setBadgeRow(null);
    setBadgeStatus(null);
    setIssuedBadge(null);
  };

  const handleIssueBadge = async () => {
    setBadgeBusy(true);
    try {
      const res = await MemberBadgeService.issueBadge(badgeRow.id);
      setIssuedBadge(res.data);
      toast.success(`Badge issued for ${badgeRow.fullname}.`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to issue badge.');
    } finally {
      setBadgeBusy(false);
    }
  };

  const handleRevokeBadge = async () => {
    setBadgeBusy(true);
    try {
      await MemberBadgeService.revokeBadge(badgeRow.id, 'Revoked by admin');
      toast.success(`Badge revoked for ${badgeRow.fullname}.`);
      setBadgeStatus({ status: 'revoked' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to revoke badge.');
    } finally {
      setBadgeBusy(false);
    }
  };

  const printBadge = () => {
    const win = window.open('', '_blank', 'width=420,height=560');
    if (!win) return;
    win.document.write(`
      <html>
        <head><title>Staff Badge - ${badgeRow.fullname}</title></head>
        <body style="text-align:center;font-family:sans-serif;padding:24px;">
          <h2 style="margin-bottom:4px;">${badgeRow.fullname}</h2>
          <p style="color:#666;margin-top:0;">${ROLE_LABELS[badgeRow.role?.role_name] || badgeRow.role?.role_name || ''}</p>
          <img src="${issuedBadge.qrDataUrl}" style="width:220px;height:220px;" alt="Staff badge QR" />
          <p style="font-size:28px;font-weight:bold;letter-spacing:4px;margin:8px 0;">${issuedBadge.code}</p>
          <p style="font-size:12px;color:#666;">Scan the QR, or enter the code manually, to sign in to the Patient Documentation tablet.</p>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  const renderBadgeModalBody = () => {
    if (badgeLoading) {
      return <p className="text-sm text-neutral-500 dark:text-neutral-500">Loading badge status…</p>;
    }
    if (issuedBadge) {
      return (
        <div className="text-center space-y-3">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg p-3 text-left dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20">
            <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              This code is shown only once and cannot be retrieved again. Print or save it now — reissuing invalidates any
              previous badge.
            </span>
          </div>
          <img
            src={issuedBadge.qrDataUrl}
            alt="Staff badge QR"
            className="mx-auto w-48 h-48 border border-neutral-200 rounded-lg dark:border-neutral-700"
          />
          <p className="text-2xl font-bold tracking-[0.3em] text-neutral-900 dark:text-neutral-50">{issuedBadge.code}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-500">Staff scan the QR, or enter the code manually, to sign in to the Patient Documentation tablet.</p>
        </div>
      );
    }
    let statusLine = 'No active badge issued for this staff member.';
    if (badgeStatus?.status === 'active') {
      const issuedAtLabel = badgeStatus.issuedAt ? new Date(badgeStatus.issuedAt).toLocaleString() : '';
      statusLine = `Active badge issued ${issuedAtLabel}.`;
    }
    return (
      <div className="space-y-3">
        <p className="text-sm text-neutral-600 dark:text-neutral-300">{statusLine}</p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500">
          Badges let staff sign in to the Patient Documentation tablet by scanning a printed QR code instead of typing a
          password.
        </p>
      </div>
    );
  };

  const headCells = [
    {
      id: 'fullname',
      label: 'User',
      render: (row) => (
        <div>
          <div className="font-bold text-neutral-800 dark:text-neutral-100">{row.fullname}</div>
          <div className="text-xs text-neutral-500 mt-0.5 dark:text-neutral-500">{row.email}</div>
        </div>
      ),
    },
    {
      id: 'role.role_name',
      label: 'Role',
      render: (row) => (
        <Badge variant={row.role?.role_name === 'admins' ? 'red' : 'blue'}>
          {ROLE_LABELS[row.role?.role_name] || row.role?.role_name || '—'}
        </Badge>
      ),
    },
    {
      id: 'accessTierId',
      label: 'Access Tier',
      render: (row) => (
        <select
          className="form-input text-xs py-1 bg-white dark:bg-ink-900"
          value={row.accessTierId || ''}
          onChange={async (e) => {
            const accessTierId = e.target.value ? Number(e.target.value) : null;
            try {
              await TeamMemberService.update(row.id, { accessTierId });
              setRows((prev) => prev.map((r) => (r.id === row.id
                ? { ...r, accessTierId, access_tier: accessTiers.find((t) => t.id === accessTierId) }
                : r)));
              toast.success(`Access tier updated for ${row.fullname}.`);
            } catch {
              toast.error('Failed to update access tier.');
            }
          }}
        >
          <option value="">Unassigned</option>
          {accessTiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      ),
    },
    { id: 'mobile', label: 'Mobile' },
    { id: 'address', label: 'Address' },
    {
      id: 'lastSeenAt',
      label: 'Last Seen',
      render: (row) => (
        <span className="text-neutral-500 text-xs dark:text-neutral-500">
          {row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleString() : 'Never'}
        </span>
      ),
    },
  ];

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <Sidebar title="User Management">
      <section className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold text-neutral-800 dark:text-neutral-100">Platform Users</h3>
            <p className="text-xs text-neutral-500 mt-1 dark:text-neutral-500">
              Create response team users, assign roles and manage credentials. All changes are audit-logged.
            </p>
          </div>
          <Button icon={Plus} onClick={openCreate}>Add User</Button>
        </div>

        <DataTable
          headCells={headCells}
          rows={rows}
          onEdit={openEdit}
          onDelete={(row) => setDeleteRow(row)}
          moreOptions={[
            {
              title: 'Reset Password',
              handler: (row) => {
                setResetRow(row);
                setResetPassword('');
              },
            },
            {
              title: 'Manage Badge',
              handler: (row) => openBadge(row),
            },
          ]}
          loading={loading}
          emptyLabel={loading ? 'Loading users…' : 'No users found.'}
        />
      </section>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editingId ? 'Edit User' : 'Add User'}
        subtitle="User accounts control access to the dashboard and mobile app."
        confirmLabel={editingId ? 'Save Changes' : 'Create User'}
        onConfirm={handleSave}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="form-label" htmlFor="u-name">Full Name *</label>
            <input id="u-name" className="form-input" value={form.fullname} onChange={setField('fullname')} />
          </div>
          <div>
            <label className="form-label" htmlFor="u-email">Email *</label>
            <input id="u-email" type="email" className="form-input" value={form.email} onChange={setField('email')} />
          </div>
          <div>
            <label className="form-label" htmlFor="u-role">Role *</label>
            <select id="u-role" className="form-input" value={form.roleId} onChange={setField('roleId')}>
              <option value="">Select role…</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{ROLE_LABELS[r.role_name] || r.role_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="u-access-tier">Access Tier *</label>
            <select id="u-access-tier" className="form-input" value={form.accessTierId} onChange={setField('accessTierId')}>
              <option value="">Select access tier…</option>
              {accessTiers.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="u-status">Availability Status</label>
            <select id="u-status" className="form-input" value={form.memberStatusId} onChange={setField('memberStatusId')}>
              <option value="">Select status…</option>
              {statuses.map((st) => (
                <option key={st.id} value={st.id}>{st.status}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="u-mobile">Mobile</label>
            <input id="u-mobile" className="form-input" value={form.mobile} onChange={setField('mobile')} />
          </div>
          <div>
            <label className="form-label" htmlFor="u-gender">Gender</label>
            <select id="u-gender" className="form-input" value={form.gender} onChange={setField('gender')}>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="u-address">Address</label>
            <input id="u-address" className="form-input" value={form.address} onChange={setField('address')} />
          </div>
          <div>
            <label className="form-label" htmlFor="u-experience">Experience (years)</label>
            <input id="u-experience" type="number" min="0" className="form-input" value={form.experience} onChange={setField('experience')} />
          </div>
          <div>
            <label className="form-label" htmlFor="u-emirateid">Emirates ID</label>
            <input id="u-emirateid" className="form-input" value={form.emirateId} onChange={setField('emirateId')} />
          </div>
          {!editingId && (
            <div>
              <label className="form-label" htmlFor="u-password">Initial Password *</label>
              <input id="u-password" type="password" className="form-input" value={form.password} onChange={setField('password')} />
              <PasswordRequirements value={form.password} />
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={Boolean(resetRow)}
        onClose={() => setResetRow(null)}
        title="Reset Password"
        subtitle={resetRow ? `${resetRow.fullname} — ${resetRow.email}` : ''}
        confirmLabel="Reset Password"
        onConfirm={handleResetPassword}
      >
        <div>
          <label className="form-label" htmlFor="u-reset-password">
            <span className="inline-flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> New Password</span>
          </label>
          <input
            id="u-reset-password"
            type="password"
            className="form-input"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
          />
          <PasswordRequirements value={resetPassword} />
          <p className="text-xs text-neutral-400 mt-2 dark:text-neutral-500">
            Share the new password securely. The user should change it after first sign-in.
          </p>
        </div>
      </Modal>

      <Modal
        open={Boolean(badgeRow)}
        onClose={closeBadge}
        title="Staff Badge"
        subtitle={badgeRow ? `${badgeRow.fullname} — ${badgeRow.email}` : ''}
        footer={
          <>
            <Button variant="ghost" onClick={closeBadge}>Close</Button>
            {issuedBadge ? (
              <Button variant="secondary" icon={Printer} onClick={printBadge}>Print Badge</Button>
            ) : (
              <>
                {badgeStatus?.status === 'active' && (
                  <Button variant="danger" onClick={handleRevokeBadge} disabled={badgeBusy}>Revoke Badge</Button>
                )}
                <Button variant="primary" icon={IdCard} onClick={handleIssueBadge} disabled={badgeBusy}>
                  {badgeStatus?.status === 'active' ? 'Reissue Badge' : 'Issue Badge'}
                </Button>
              </>
            )}
          </>
        }
      >
        {renderBadgeModalBody()}
      </Modal>

      <Modal
        open={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        title="Delete User"
        subtitle={deleteRow ? `${deleteRow.fullname} — ${deleteRow.email}` : ''}
        confirmLabel="Delete"
        onConfirm={handleDelete}
      >
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          This permanently removes the user account and revokes access. This action cannot be undone.
        </p>
      </Modal>
    </Sidebar>
  );
}
